(() => {
  const DATE_OPTION_PATTERN = /(\d{4})年\s*(\d{1,2})月\s*(\d{1,2})日(?:\s*[\(（]([日月火水木金土])[\)）])?/;
  const DELIVERY_LABEL_PATTERN = /お届け希望日|配達希望日|配送希望日|delivery date/i;
  const NO_DATE_PATTERN = /指定なし|指定しない|なし|no date/i;
  const ERROR_SELECTOR = '[data-shipco-delivery-date-filter-error]';
  const LEAD_TIME_NOTE_SELECTOR = '[data-yamato-delivery-lead-time-note]';
  const LEAD_TIME_NOTE_STYLE_ID = 'yamato-delivery-lead-time-note-style';
  const DELIVERY_DAYS_AFTER_SHIPPING = 1;
  const observedRoots = new WeakSet();

  const JAPAN_HOLIDAYS = new Set([
    // Cabinet Office published holidays and substitute holidays for 2026.
    '2026-01-01',
    '2026-01-12',
    '2026-02-11',
    '2026-02-23',
    '2026-03-20',
    '2026-04-29',
    '2026-05-03',
    '2026-05-04',
    '2026-05-05',
    '2026-05-06',
    '2026-07-20',
    '2026-08-11',
    '2026-09-21',
    '2026-09-22',
    '2026-09-23',
    '2026-10-12',
    '2026-11-03',
    '2026-11-23',
    // Cabinet Office published holidays and substitute holidays for 2027.
    '2027-01-01',
    '2027-01-11',
    '2027-02-11',
    '2027-02-23',
    '2027-03-21',
    '2027-03-22',
    '2027-04-29',
    '2027-05-03',
    '2027-05-04',
    '2027-05-05',
    '2027-07-19',
    '2027-08-11',
    '2027-09-20',
    '2027-09-23',
    '2027-10-11',
    '2027-11-03',
    '2027-11-23',
  ]);

  const toIsoDate = (year, month, day) =>
    `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const normalizeDate = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const addDays = (date, days) => {
    const nextDate = normalizeDate(date);
    nextDate.setDate(nextDate.getDate() + days);
    return nextDate;
  };

  const isStoreClosedForShipping = (date) => {
    const weekday = date.getDay();
    if (weekday === 0 || weekday === 6) return true;

    return JAPAN_HOLIDAYS.has(toIsoDate(date.getFullYear(), date.getMonth() + 1, date.getDate()));
  };

  const getEarliestShippingDate = () => {
    let shippingDate = addDays(new Date(), 1);

    while (isStoreClosedForShipping(shippingDate)) {
      shippingDate = addDays(shippingDate, 1);
    }

    return shippingDate;
  };

  const getEarliestDeliveryDate = () => addDays(getEarliestShippingDate(), DELIVERY_DAYS_AFTER_SHIPPING);

  const parseDateOption = (option) => {
    const text = option.textContent || '';
    const match = text.match(DATE_OPTION_PATTERN);

    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const weekdayLabel = match[4] || '';
    const date = new Date(year, month - 1, day);

    if (Number.isNaN(date.getTime())) return null;
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;

    return {
      isoDate: toIsoDate(year, month, day),
      date,
      weekday: date.getDay(),
      weekdayLabel,
    };
  };

  const isBlockedDateOption = (option) => {
    const optionDate = parseDateOption(option);

    if (!optionDate) return false;
    if (normalizeDate(optionDate.date) < getEarliestDeliveryDate()) return true;

    return false;
  };

  const getExplicitLabelText = (select) => {
    if (!select.id) return '';

    try {
      const escapedId = window.CSS?.escape ? CSS.escape(select.id) : select.id.replace(/"/g, '\\"');
      return document.querySelector(`label[for="${escapedId}"]`)?.textContent || '';
    } catch {
      return '';
    }
  };

  const getNearbyText = (select) => {
    const label = select.labels?.[0]?.textContent || '';
    const labelledBy = select.getAttribute('aria-labelledby');
    const ariaLabelledBy = labelledBy ? document.getElementById(labelledBy)?.textContent || '' : '';
    const explicitLabel = getExplicitLabelText(select);
    const wrapperText = select.closest('label, .field, .form-field, div')?.textContent || '';

    return [label, ariaLabelledBy, explicitLabel, select.getAttribute('aria-label') || '', wrapperText].join(' ');
  };

  const looksLikeDeliveryDateSelect = (select) => {
    const options = Array.from(select.options);
    const dateOptions = options.filter((option) => parseDateOption(option));

    if (dateOptions.length < 2) return false;

    const nearbyText = getNearbyText(select);
    if (DELIVERY_LABEL_PATTERN.test(nearbyText)) return true;

    return options.some((option) => NO_DATE_PATTERN.test(option.textContent || ''));
  };

  const collectSearchRoots = (root = document) => {
    const roots = [root];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);

    while (walker.nextNode()) {
      const element = walker.currentNode;

      if (element instanceof HTMLElement && element.shadowRoot) {
        roots.push(...collectSearchRoots(element.shadowRoot));
      }
    }

    return roots;
  };

  const getErrorHost = (select) => {
    let candidate = select.parentElement;

    for (let depth = 0; depth < 5 && candidate; depth += 1) {
      const selectCount = candidate.querySelectorAll?.('select').length || 0;
      const text = candidate.textContent || '';
      const hasDeliveryContext = DELIVERY_LABEL_PATTERN.test(text) || text.includes('お届け希望時間');

      if (selectCount >= 2 && hasDeliveryContext) return candidate;

      candidate = candidate.parentElement;
    }

    return select.parentElement || select;
  };

  const ensureLeadTimeNoteStyle = (root) => {
    const ownerDocument = root.ownerDocument || document;
    const styleRoot = root instanceof ShadowRoot ? root : ownerDocument.head;

    if (styleRoot.getElementById?.(LEAD_TIME_NOTE_STYLE_ID)) return;

    const style = ownerDocument.createElement('style');
    style.id = LEAD_TIME_NOTE_STYLE_ID;
    style.textContent = `
      .yamato-delivery-lead-time-note {
        box-sizing: border-box;
        display: block;
        grid-column: 1 / -1;
        flex-basis: 100%;
        width: 100%;
        margin-block-start: 12px;
        color: currentColor;
        font-size: 12px;
        line-height: 1.55;
      }

      .yamato-delivery-lead-time-note__title {
        margin: 0 0 7px;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.4;
      }

      .yamato-delivery-lead-time-note__table {
        width: 100%;
        border-collapse: collapse;
        border: 1px solid rgb(0 0 0 / 0.12);
        border-radius: 6px;
        overflow: hidden;
      }

      .yamato-delivery-lead-time-note__table th,
      .yamato-delivery-lead-time-note__table td {
        padding: 7px 8px;
        border-block-end: 1px solid rgb(0 0 0 / 0.1);
        vertical-align: top;
        text-align: left;
      }

      .yamato-delivery-lead-time-note__table tr:last-child th,
      .yamato-delivery-lead-time-note__table tr:last-child td {
        border-block-end: 0;
      }

      .yamato-delivery-lead-time-note__table th {
        width: 45%;
        background: rgb(0 0 0 / 0.035);
        font-weight: 600;
      }

      .yamato-delivery-lead-time-note__table a {
        color: currentColor;
        text-decoration: underline;
        text-underline-offset: 0.18em;
      }

      .yamato-delivery-lead-time-note__text {
        margin: 7px 0 0;
        color: rgb(0 0 0 / 0.68);
        font-size: 11px;
      }

      .yamato-delivery-lead-time-note__text p {
        margin: 0;
      }

      .yamato-delivery-lead-time-note__text p + p {
        margin-top: 4px;
      }

      .yamato-delivery-lead-time-note__alert {
        margin: 7px 0 0;
        color: #b00020;
        font-size: 11px;
        font-weight: 600;
        line-height: 1.55;
      }
    `;

    styleRoot.appendChild(style);
  };

  const createLeadTimeNote = (ownerDocument) => {
    const note = ownerDocument.createElement('div');
    note.className = 'yamato-delivery-lead-time-note';
    note.dataset.yamatoDeliveryLeadTimeNote = 'true';
    note.setAttribute('aria-label', '地域別お届け日数の目安');
    note.innerHTML = `
      <p class="yamato-delivery-lead-time-note__title">地域別お届け日数の目安</p>
      <table class="yamato-delivery-lead-time-note__table">
        <tbody>
          <tr>
            <th scope="row">九州</th>
            <td>発送日から1日〜</td>
          </tr>
          <tr>
            <th scope="row">中国・四国・関西</th>
            <td>発送日から1〜2日</td>
          </tr>
          <tr>
            <th scope="row">中部・北陸・信越・関東</th>
            <td>発送日から2日〜</td>
          </tr>
          <tr>
            <th scope="row">東北</th>
            <td>発送日から2〜3日</td>
          </tr>
          <tr>
            <th scope="row">北海道・沖縄・離島など</th>
            <td>発送日から3日〜</td>
          </tr>
        </tbody>
      </table>
      <div class="yamato-delivery-lead-time-note__text">
        <p>※発送準備に1営業日いただきます。</p>
        <p>※当店の発送業務は、土日祝日を除く平日のみ行っております。</p>
        <p>※お届け地域、交通事情、悪天候などにより、お届け日が前後する場合があります。</p>
      </div>
      <p class="yamato-delivery-lead-time-note__text">
        ※土日祝日にいただいたご注文は、翌営業日より順次発送準備を進めます。あらかじめご了承ください。
      </p>
    `;

    return note;
  };

  const ensureLeadTimeNote = (select) => {
    const host = getErrorHost(select);

    if (host.querySelector?.(LEAD_TIME_NOTE_SELECTOR)) return;

    const root = host.getRootNode();
    ensureLeadTimeNoteStyle(root);
    host.appendChild(createLeadTimeNote(host.ownerDocument || document));
  };

  const ensureErrorElement = (select) => {
    const host = getErrorHost(select);
    const existing = host.querySelector?.(ERROR_SELECTOR);
    if (existing) return existing;

    const error = document.createElement('p');
    error.hidden = true;
    error.dataset.shipcoDeliveryDateFilterError = 'true';
    error.setAttribute('role', 'alert');
    error.style.boxSizing = 'border-box';
    error.style.display = 'block';
    error.style.gridColumn = '1 / -1';
    error.style.flexBasis = '100%';
    error.style.width = '100%';
    error.style.margin = '8px 0 0';
    error.style.padding = '0';
    error.style.color = '#b00020';
    error.style.fontSize = '12px';
    error.style.lineHeight = '1.5';
    error.style.pointerEvents = 'none';
    error.textContent = '最短お届け日以降の日付をご指定ください。';

    host.appendChild(error);

    return error;
  };

  const showError = (select) => {
    const error = ensureErrorElement(select);
    error.hidden = false;
  };

  const hideError = () => {
    document.querySelectorAll(ERROR_SELECTOR).forEach((error) => {
      error.hidden = true;
    });
  };

  const resetSelectToEmptyOption = (select) => {
    const emptyOption = Array.from(select.options).find((option) => NO_DATE_PATTERN.test(option.textContent || ''));

    if (!emptyOption) {
      select.selectedIndex = 0;
    } else {
      select.value = emptyOption.value;
    }

    select.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const filterSelectOptions = (select) => {
    if (!looksLikeDeliveryDateSelect(select)) return false;

    ensureLeadTimeNote(select);

    const selectedOption = select.selectedOptions[0] || null;
    const selectedOptionWasBlocked = selectedOption ? isBlockedDateOption(selectedOption) : false;
    let removedCount = 0;

    Array.from(select.options).forEach((option) => {
      if (!isBlockedDateOption(option)) return;

      option.remove();
      removedCount += 1;
    });

    if (selectedOptionWasBlocked) {
      resetSelectToEmptyOption(select);
    }

    if (removedCount > 0) {
      select.dataset.shipcoDeliveryDateFiltered = 'true';
    }

    return removedCount > 0;
  };

  const getDeliveryDateSelects = () =>
    collectSearchRoots()
      .flatMap((root) => Array.from(root.querySelectorAll('select')))
      .filter((select) => looksLikeDeliveryDateSelect(select));

  const filterAllSelects = () => {
    collectSearchRoots().forEach((root) => {
      if (observedRoots.has(root)) return;

      observedRoots.add(root);
      observer.observe(root, { childList: true, subtree: true });
    });

    getDeliveryDateSelects().forEach((select) => filterSelectOptions(select));
  };

  const validateDeliveryDateSelection = () => {
    const invalidSelect = getDeliveryDateSelects().find((select) => {
      const selectedOption = select.selectedOptions[0];
      return selectedOption ? isBlockedDateOption(selectedOption) : false;
    });

    if (!invalidSelect) {
      hideError();
      return true;
    }

    resetSelectToEmptyOption(invalidSelect);
    showError(invalidSelect);
    invalidSelect.focus();

    return false;
  };

  const scheduleFilter = (() => {
    let frame = 0;

    return () => {
      if (frame) return;

      frame = window.requestAnimationFrame(() => {
        frame = 0;
        filterAllSelects();
      });
    };
  })();

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    if (!looksLikeDeliveryDateSelect(event.target)) return;

    if (validateDeliveryDateSelection()) {
      hideError();
    }
  });

  document.addEventListener(
    'submit',
    (event) => {
      if (!(event.target instanceof HTMLFormElement)) return;
      if (event.target.id !== 'cart-form') return;
      if (validateDeliveryDateSelection()) return;

      event.preventDefault();
      event.stopPropagation();
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const checkoutButton = event.target instanceof Element ? event.target.closest('[name="checkout"]') : null;
      if (!checkoutButton) return;
      if (validateDeliveryDateSelection()) return;

      event.preventDefault();
      event.stopPropagation();
    },
    true
  );

  const observer = new MutationObserver(scheduleFilter);

  const initialize = () => {
    filterAllSelects();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
